/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/camelcase */
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  makeStyles,
  Paper,
  Switch,
  Tooltip,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import clsx from 'clsx';
import { Formik } from 'formik';
import i18n from 'i18n';
import React from 'react';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { GenericFormItem } from 'skyone-lib/lib/GenericForm/interfaces';
import Permission from 'components/ValidPermission';
import Schema from './schema';
import styles from './styles';

const useStyles = makeStyles(styles);

interface Props {
  error?: boolean;
  fields: GenericFormItem[];
  initialValues: { [key: string]: any };
  labelSwitch?: string;
  ldapLoading?: boolean;
  ldapType?: string;
  onField?: boolean;
  title: string;
  tooltipHelper?: string;
  onCancel?: (values: { [key: string]: any }) => void;
  onError?: (error) => void;
  onSubmit: (values: { [key: string]: any }) => void;
  onTestClick?: (values: { [key: string]: any }) => void;
}

const FormBox = ({
  error,
  fields,
  initialValues,
  labelSwitch,
  ldapLoading = false,
  ldapType,
  onField = false,
  title,
  tooltipHelper,
  onCancel,
  onError,
  onSubmit,
  onTestClick,
}: Props) => {
  const classes = useStyles();
  const [values, setValues] = React.useState(initialValues);
  const [valuesSaved, setValuesSaved] = React.useState(initialValues);
  const [hasError, setHasError] = React.useState(error);

  const handleSave = (values: any) => {
    if (onField) {
      const onlyOneEmpty = Object.keys(values).some(key => {
        return values[key] !== '';
      });

      setHasError(!onlyOneEmpty);

      !!onError && onError(!onlyOneEmpty);
      if (!onlyOneEmpty) return;

      setHasError(false);

      !!onSubmit &&
        onSubmit({
          ...values,
        });

      return;
    }

    const haveEmpty = Object.keys(values).some(key => {
      if (['compartment_id'].includes(key)) return false;
      return values[key] === '' || values[key] === null;
    });

    setHasError(haveEmpty);

    !!onError && onError(haveEmpty);
    if (haveEmpty) return;

    !!onSubmit &&
      onSubmit({
        ...values,
        ldap_type: ldapType,
      });

    setHasError(false);
  };

  const handleClear = (resetForm: any) => {
    resetForm();

    const values = {};
    Object.keys(valuesSaved).forEach(key => {
      values[key] = '';
    });

    setValuesSaved(values);
    onCancel?.(values);
  };

  React.useEffect(() => {
    setHasError(error);
  }, [error]);

  const handleTestLDAP = values => {
    const haveEmpty = Object.keys(values).some(key => {
      return values[key] === '' || values[key] === null;
    });

    setHasError(haveEmpty);
    if (haveEmpty) return;
    !!onTestClick && onTestClick(values);
  };

  const handleGetValue = (key: string, value: any) => {
    const dataProps = {
      ...values,
      [key]: value,
    };
    setValues(dataProps);
    handleSave(dataProps);
  };

  return (
    <Formik
      initialValues={valuesSaved}
      onSubmit={handleSave}
      enableReinitialize
      validationSchema={Schema}
    >
      {({ setFieldValue, errors, values, resetForm }) => (
        <Paper
          elevation={2}
          className={clsx(classes.root, {
            [classes.error]: hasError,
          })}
        >
          <DialogTitle className={classes.header}>
            {title}
            {tooltipHelper && (
              <Tooltip title={tooltipHelper}>
                <HelpIcon fontSize="small" />
              </Tooltip>
            )}
          </DialogTitle>

          <DialogContent dividers className={classes.content}>
            <Grid item container spacing={2} className={classes.contentFields}>
              <GenericForm
                changeField={({ key, value }) => handleGetValue(key, value)}
                fields={values}
                screenFields={fields}
                setFieldValue={setFieldValue}
                errors={errors}
              />
            </Grid>
          </DialogContent>

          <DialogActions className={classes.footer}>
            {labelSwitch && (
              <Grid item style={{ flex: 1 }}>
                <FormControlLabel
                  classes={{ label: classes.switchLabel }}
                  control={
                    <Switch
                      checked={values.switch}
                      onChange={(e, value) => setFieldValue('switch', value)}
                      classes={{ colorSecondary: classes.switchChecked }}
                      size="small"
                    />
                  }
                  label={labelSwitch}
                />
              </Grid>
            )}
            {onTestClick && (
              <Permission value="can_test_ldap">
                <Button
                  disabled={ldapLoading}
                  id="test-button"
                  onClick={() => handleTestLDAP(values)}
                  className={classes.buttonTest}
                >
                  {ldapLoading ? (
                    <CircularProgress size={15} />
                  ) : (
                    i18n.t('TEST_KEY')
                  )}
                </Button>
              </Permission>
            )}
            <Button id="clear-button" onClick={() => handleClear(resetForm)}>
              {i18n.t('CLEAR_FORM_KEY')}
            </Button>
          </DialogActions>
        </Paper>
      )}
    </Formik>
  );
};

export default FormBox;
